/* eslint-disable  @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { PayAtTableNiPayload } from '../models/pay-at-table.model';
import { GeideaCallbackData, GeideaCheckoutSession, GeideaValidationPayload, MastercardPayload } from '../models/payment.model';
import { ApiService } from './api.service';

export interface GeideaDropinHandlers {
  onSuccess: (data: GeideaCallbackData) => void;
  onError: (data: GeideaCallbackData) => void;
  onCancel: () => void;
}

/**
 * Geidea Payment Gateway (Checkout v2) — drop-in mode.
 *
 * The session endpoint returns a session id plus the region-specific checkout
 * JS URL; startPayment's 3rd argument is a container element id, which tells
 * the SDK to render the checkout in place instead of opening a popup. Card
 * fields stay inside Geidea's embedded iframe, so no PCI scope for us.
 */
@Injectable({
  providedIn: 'root'
})
export class GeideaCheckoutService {
  private apiService = inject(ApiService);
  private loadedUrl: string | null = null;

  public getSession(payload: MastercardPayload | PayAtTableNiPayload): Observable<GeideaCheckoutSession> {
    return this.apiService.getGeideaCheckoutSession(payload);
  }

  public async mount(containerId: string, session: GeideaCheckoutSession, handlers: GeideaDropinHandlers): Promise<boolean> {
    const ok = await this.setupScript(session.js_url);
    if (!ok || !(window as any).GeideaCheckout) {
      return false;
    }

    const payment = new (window as any).GeideaCheckout(handlers.onSuccess, handlers.onError, handlers.onCancel);
    payment.startPayment(session.session_id, null, containerId);
    return true;
  }

  public unmount(containerId: string): void {
    document.getElementById(containerId)?.replaceChildren();
  }

  private setupScript(jsUrl: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.loadedUrl === jsUrl) {
        resolve(true);
        return;
      }
      const checkoutScript = document.createElement('script');
      checkoutScript.src = jsUrl;
      checkoutScript.type = 'text/javascript';
      checkoutScript.onload = () => {
        this.loadedUrl = jsUrl;
        resolve(true);
      };
      checkoutScript.onerror = () => resolve(false);
      document.body.appendChild(checkoutScript);
    });
  }

  public validateGeideaSession(payload: GeideaValidationPayload): Observable<boolean> {
    return this.apiService.validateGeideaCheckout(payload).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
